import type { As2DlqTaskRecord } from '../types/api';

export type DlqGroupBy = 'none' | 'partner' | 'reason';

export interface DlqTaskGroup {
  key: string;
  label: string;
  tasks: As2DlqTaskRecord[];
}

// Best-available "reason" bucket for a DLQ task: retryCategory is the coarsest, most stable
// classification when present; deadLetterReason/lastError are free text fallbacks for tasks
// that haven't been categorized. Exported so the UI can show the same label used for grouping.
export function dlqReasonLabel(task: As2DlqTaskRecord): string {
  return task.retryCategory ?? task.deadLetterReason ?? task.lastError ?? 'Unknown reason';
}

// Clusters DLQ tasks by partner or failure reason so an operator can see "50 tasks failed for
// the same reason" instead of 50 identical-looking rows. Groups are sorted by size (largest
// cluster first) since that's the most actionable triage order. Returns [] for groupBy:'none'
// — callers should render the flat list in that case.
export function groupDlqTasks(tasks: As2DlqTaskRecord[], groupBy: DlqGroupBy): DlqTaskGroup[] {
  if (groupBy === 'none') return [];

  const groups = new Map<string, DlqTaskGroup>();
  for (const task of tasks) {
    const key = groupBy === 'partner' ? (task.partnerCode ?? 'Unknown partner') : dlqReasonLabel(task);
    const existing = groups.get(key);
    if (existing) {
      existing.tasks.push(task);
    } else {
      groups.set(key, { key, label: key, tasks: [task] });
    }
  }

  return Array.from(groups.values()).sort((a, b) => b.tasks.length - a.tasks.length);
}
